import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Paper, IconButton, MenuItem,
    FormControl, Select, CircularProgress, Alert, InputLabel
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GetAppIcon from "@mui/icons-material/GetApp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import { axios_tenant } from "../../../../../axios";
import Button from "../../../../shared/OAWorkflow/components/atoms/Button";

const BASE_URL = "/collaboration/workflow";

/**
 * Reusable Upload UI component that matches the Figma design.
 * Features: Refined dropzone, file selection view with icons, Client dropdown inside the card, 
 * and Submit button outside at bottom right.
 */
const OAWorkflowUploadUI = ({ onUploadSuccess }) => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState("");
    const [loadingClients, setLoadingClients] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);

    // Fetch OA Clients
    useEffect(() => {
        axios_tenant.get(`${BASE_URL}/intake/clients`)
            .then(res => {
                setClients(res.data.clients || []);
                setLoadingClients(false);
            })
            .catch(err => {
                console.error("Failed to fetch clients:", err);
                setLoadingClients(false);
            });
    }, []);

    const onDrop = (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
            setError(null);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ".xls,.xlsx,.csv",
        maxFiles: 1
    });

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append("file", file);
            if (selectedClient) {
                formData.append("clientName", selectedClient);
            }

            const res = await axios_tenant.post(`${BASE_URL}/intake/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                silent: true
            });

            setUploadResult(res.data.data);
            if (onUploadSuccess) {
                onUploadSuccess(res.data.data);
            }
            setFile(null);
            setSelectedClient("");
        } catch (err) {
            setError(err?.response?.data?.message || "Upload failed");
            // If it's a 422 Failed (Imported=0), we might have data in response.data.data
            if (err?.response?.data?.data) {
                setUploadResult(err.response.data.data);
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadReport = async (batchId) => {
        try {
            const response = await axios_tenant.get(`${BASE_URL}/intake/batches/${batchId}/export`, {
                responseType: 'blob',
                silent: true
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Validation_Result_Batch_${batchId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Failed to download validation report:", err);
            setError("Failed to download validation report.");
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios_tenant.get(`${BASE_URL}/intake/template`, {
                responseType: 'blob',
                silent: true
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'OA_Intake_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Failed to download template:", err);
            setError("Failed to download template. Please try again.");
        }
    };

    return (
        <Box sx={{ p: 4 }} data-testid="oa-modern-upload-ui">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ color: "#1E293B", fontSize: "20px", fontWeight: 400 }}>
                    Upload File
                </Typography>
                <Button
                    variant="secondary"
                    startIcon={<GetAppIcon />}
                    onClick={handleDownloadTemplate}
                    sx={{ border: 'none' }}
                >
                    Download Template
                </Button>
            </Box>

            {/* Main Content Card (White area) */}
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    mb: 4
                }}
            >
                {/* Dashed Dropzone Area */}
                <Box
                    {...getRootProps()}
                    sx={{
                        p: 4,
                        border: "1px dashed #E0E0E0",
                        borderRadius: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        mb: file ? 3 : 0,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "#005CB9", backgroundColor: "#F2F7FC" }
                    }}
                >
                    <input {...getInputProps()} />
                    <DescriptionIcon sx={{ fontSize: 32, color: "#005CB9", mb: 1.5, opacity: 0.8 }} />
                    <Typography variant="body1" sx={{ color: "#64748B", fontWeight: 400 }}>
                        {isDragActive ? (
                            "Drop the file here"
                        ) : (
                            <>
                                <span style={{ color: "#005CB9", textDecoration: "underline", fontWeight: 500 }}>Click to upload</span> or drag and drop
                            </>
                        )}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94A3B8", mt: 0.5 }}>
                        XLS, XLSX or CSV (max. 3MB)
                    </Typography>
                </Box>

                {/* Selection Details (Inside the white card) */}
                {file && (
                    <Box>
                        {/* Selected File Row */}
                        <Box sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            p: 1.5, px: 2, border: "1px solid #F0F0F0", borderRadius: 1.5, mb: 3,
                            backgroundColor: "#FAFAFA"
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <DescriptionIcon sx={{ color: "#005CB9" }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {(file.size / 1024).toFixed(0)}kb
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">•</Typography>
                                        <Typography variant="caption" color="success.main" fontWeight={500}>Complete</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                                    <DeleteIcon sx={{ fontSize: 20, color: "#999" }} />
                                </IconButton>
                                <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 22 }} />
                            </Box>
                        </Box>

                        {/* Client Dropdown (Standard MUI style like User Management) */}
                        <Box sx={{ width: "45%" }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="trexo-client-label">Trexo Client</InputLabel>
                                <Select
                                    labelId="trexo-client-label"
                                    value={selectedClient}
                                    label="Trexo Client"
                                    onChange={(e) => setSelectedClient(e.target.value)}
                                    disabled={loadingClients}
                                    sx={{ borderRadius: 1.5 }}
                                >
                                    {clients.map((client) => (
                                        <MenuItem key={client.subscriptionId} value={client.tenantCode}>
                                            {client.tenantName} ({client.tenantCode})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Submit Button (Bottom right, outside the white card) */}
            {file && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: -2 }}>
                    <Typography variant="body2" color={selectedClient ? "transparent" : "error.main"}>
                        {!selectedClient && "* Please select a Trexo Client to continue"}
                    </Typography>
                    <Button
                        variant="primary"
                        onClick={handleUpload}
                        disabled={uploading || !selectedClient}
                        sx={{ px: 5 }}
                    >
                        {uploading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
                    </Button>
                </Box>
            )}

            {error && !uploadResult && <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>{error}</Alert>}

            {uploadResult && (
                <Box sx={{ mt: 3 }}>
                    {uploadResult.imported > 0 && uploadResult.rejected === 0 && (
                        <Alert
                            severity="success"
                            sx={{ borderRadius: 1.5, fontWeight: 500 }}
                            action={
                                <Button
                                    variant="secondary"
                                    startIcon={<GetAppIcon />}
                                    onClick={() => handleDownloadReport(uploadResult.batchId)}
                                    sx={{ border: 'none', color: 'inherit' }}
                                >
                                    Download Report
                                </Button>
                            }
                        >
                            Import completed successfully. ({uploadResult.imported} records)
                        </Alert>
                    )}

                    {uploadResult.imported > 0 && uploadResult.rejected > 0 && (
                        <Alert
                            severity="warning"
                            sx={{ borderRadius: 1.5, fontWeight: 500 }}
                            action={
                                <Button
                                    variant="secondary"
                                    startIcon={<GetAppIcon />}
                                    onClick={() => handleDownloadReport(uploadResult.batchId)}
                                    sx={{ border: 'none', color: 'inherit' }}
                                >
                                    Download Report
                                </Button>
                            }
                        >
                            Some records could not be imported. Download report to review issues.
                        </Alert>
                    )}

                    {uploadResult.imported === 0 && uploadResult.rejected > 0 && (
                        <Alert severity="error" sx={{ borderRadius: 1.5, fontWeight: 500 }}>
                            No valid records found. Please correct and re-upload.
                        </Alert>
                    )}

                    {/* Navigation Links */}
                    <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                        <Button
                            variant="primary"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => navigate("/collaboration-space/oa-workflow")}
                        >
                            View All OA Tasks
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default OAWorkflowUploadUI;
